<template>
  <header class="theater-header" aria-label="Barra de navegación principal">
    <div class="header-inner">
      <div class="header-left">
        <a href="/" class="logo-link" aria-label="Somos Teatro">
          <!-- Se sirve desde `static/img/` y no desde el bundle del front: los
               assets del build llevan hash en el nombre, y el que había acá
               (stmain/dist/assets/MainLogo-<hash>.png) quedó en 404 al
               recompilar, así que el menú mostraba el texto alternativo. -->
          <img
            class="logo-img"
            src="/static/img/logo_ppal.png"
            alt="Somos Teatro"
          />
        </a>
      </div>

      <nav class="header-nav">
        <template v-if="canManage">
          <!-- PREVENTA: gestor de campañas, exclusivo del super admin -->
          <a v-if="isAdmin" href="/preventa/" class="header-link">PREVENTA</a>

          <!-- OBRAS -->
          <div class="nav-dd">
            <button type="button" class="header-link nav-dd-trigger">
              OBRAS <span class="caret">▾</span>
            </button>
            <div class="nav-dd-menu">
              <a href="/admin-obras/" class="nav-dd-item">MIS OBRAS</a>
              <a href="/admin-obras/nueva/" class="nav-dd-item">SUBIR NUEVA</a>
            </div>
          </div>

          <!-- RESERVAS -->
          <div class="nav-dd">
            <button type="button" class="header-link nav-dd-trigger">
              RESERVAS
              <span v-if="counts.por_confirmar" class="nav-badge">{{ counts.por_confirmar }}</span>
              <span class="caret">▾</span>
            </button>
            <div class="nav-dd-menu">
              <a href="/reservas/por-confirmar/" class="nav-dd-item">
                <span>POR CONFIRMAR</span>
                <span v-if="counts.por_confirmar" class="nav-badge">{{ counts.por_confirmar }}</span>
              </a>
              <a href="/reservas/confirmadas/" class="nav-dd-item">
                <span>CONFIRMADAS</span>
                <span v-if="counts.confirmadas" class="nav-badge">{{ counts.confirmadas }}</span>
              </a>
              <a href="/reservas/contratadas/" class="nav-dd-item">
                <span>CONTRATADAS</span>
                <span v-if="counts.contratadas" class="nav-badge">{{ counts.contratadas }}</span>
              </a>
              <a href="/reservas/rechazadas/" class="nav-dd-item">
                <span>RECHAZADAS</span>
              </a>
              <a href="/reservas/obras/" class="nav-dd-item">
                <span>POR OBRA</span>
              </a>
            </div>
          </div>

          <a href="/ventas/" class="header-link">VENTAS</a>

          <a href="/pagos/" class="header-link">PAGOS</a>
        </template>

        <template v-else-if="isAuthenticated">
          <a href="/mis-reservas/" class="header-link">MIS RESERVAS</a>
          <a href="/pagos/" class="header-link">PAGOS</a>
          <!-- MI PERFIL no va acá: es el ícono de la derecha (header-right). -->
        </template>
        <!-- Preguntas frecuentes vive en el pie de página, no acá. -->
      </nav>

      <div class="header-right">
        <!-- Deja a la vista con qué rol se entró, sin tener que abrir el panel. -->
        <span v-if="isAdmin" class="role-badge">ADMIN</span>

        <!-- Company panel (managers) -->
        <div v-if="canManage" class="company-menu" v-click-outside="closePanel">
          <button
            type="button"
            class="company-badge"
            aria-label="Menú de la compañía"
            @click="togglePanel"
          >
            <img v-if="companyLogo" :src="companyLogo" class="company-badge-img" alt="Logo compañía" />
            <span v-else class="company-badge-fallback">{{ companyInitials }}</span>
          </button>

          <div v-if="panelOpen" class="company-panel">
            <button
              type="button"
              class="company-panel-close"
              aria-label="Cerrar"
              @click="closePanel"
            >
              ✕
            </button>
            <div class="company-panel-head">
              <div class="company-panel-name">
                {{ isAdmin ? 'Administrador' : (companyName || 'Mi compañía') }}
              </div>
              <div class="company-panel-sub">
                {{ isAdmin ? 'SOMOS TEATRO' : 'COMPAÑÍA DE TEATRO' }}
              </div>
            </div>
            <a v-if="isAdmin" href="/companias/" class="company-panel-item">COMPAÑÍAS</a>
            <a v-if="isAdmin" href="/clientes/" class="company-panel-item">CLIENTES</a>
            <a v-if="isAdmin" href="/productoras/" class="company-panel-item">PRODUCTORAS</a>
            <a v-if="isAdmin" href="/informes/" class="company-panel-item">INFORMES</a>
            <a v-else href="/mi-compania/" class="company-panel-item">PERFIL DE LA COMPAÑÍA</a>
            <!-- El catálogo de soportes lo consultan todas las compañías. -->
            <a href="/soportes/" class="company-panel-item">SOPORTES</a>
            <a href="/historial/" class="company-panel-item">HISTORIAL DE NEGOCIOS</a>
            <a href="/contratos/" class="company-panel-item">CONTRATOS</a>
            <a href="/logout/" class="company-panel-item company-panel-logout">
              <span>CERRAR SESIÓN</span>
              <v-icon size="16" color="#c8102e">mdi-close-circle</v-icon>
            </a>
          </div>
        </div>

        <!-- Cliente: el ícono despliega su perfil y el cierre de sesión. -->
        <div v-else-if="isAuthenticated" class="company-menu" v-click-outside="closePanel">
          <button
            type="button"
            class="company-badge"
            aria-label="Mi cuenta"
            @click="togglePanel"
          >
            <v-icon size="28" color="#c8102e">mdi-account</v-icon>
          </button>

          <div v-if="panelOpen" class="company-panel">
            <button
              type="button"
              class="company-panel-close"
              aria-label="Cerrar"
              @click="closePanel"
            >
              ✕
            </button>
            <div class="company-panel-head">
              <div class="company-panel-name">{{ clientName || 'Mi cuenta' }}</div>
              <div class="company-panel-sub">CLIENTE</div>
            </div>
            <a href="/mi-compania/" class="company-panel-item">MI PERFIL</a>
            <a href="/logout/" class="company-panel-item company-panel-logout">
              <span>CERRAR SESIÓN</span>
              <v-icon size="16" color="#c8102e">mdi-close-circle</v-icon>
            </a>
          </div>
        </div>
        <!-- Anónimo: un solo acceso; el tipo de cuenta se elige ya dentro
             del formulario de login. -->
        <a v-else href="/login/" class="login-btn-header">INICIAR SESIÓN</a>
      </div>
    </div>
  </header>
</template>

<script>
import { fetchMyReservations } from "@/services/teatroApi";

const STATUS_GROUPS = {
  por_confirmar: ["pending", "pending_availability", "date_requested"],
  confirmadas: ["date_confirmed"],
  contratadas: [
    "contract_pending",
    "deposit_pending",
    "deposit_paid",
    "balance_invoice_pending",
    "balance_pending",
    "show_completed",
    "balance_paid",
    "company_invoice_pending",
    "company_payment_pending",
    "company_paid",
  ],
  rechazadas: ["cancelled"],
};

export default {
  name: "TeatroMenu",
  data() {
    return {
      panelOpen: false,
      reservations: [],
    };
  },
  computed: {
    isAuthenticated() {
      return this.$store?.state?.is_authenticated === true;
    },
    isAdmin() {
      return this.$store?.state?.is_admin === true;
    },
    canManage() {
      return this.isAdmin || this.$store?.state?.role === "theater_member";
    },
    me() {
      return this.$store?.state?.me || null;
    },
    companyName() {
      return this.me?.company?.name || "";
    },
    companyLogo() {
      return this.me?.company?.logo || "";
    },
    clientName() {
      const me = this.me;
      if (!me) return "";
      return (
        me.fullName ||
        `${me.firstName || ""} ${me.lastName || ""}`.trim() ||
        me.email ||
        ""
      );
    },
    companyInitials() {
      const name = (this.companyName || "").trim();
      return name ? name.slice(0, 2).toUpperCase() : "CIA";
    },
    counts() {
      const c = { por_confirmar: 0, confirmadas: 0, contratadas: 0, rechazadas: 0 };
      for (const r of this.reservations) {
        const status = String(r.status || "").toLowerCase();
        for (const group of Object.keys(STATUS_GROUPS)) {
          if (STATUS_GROUPS[group].includes(status)) {
            c[group] += 1;
          }
        }
      }
      return c;
    },
  },
  async created() {
    try {
      await this.$store.dispatch("fetchIsAuthenticated");
    } catch (e) {
      console.error("Error fetching auth state in menu:", e);
    }
    if (this.canManage) {
      this.loadManagerData();
    }
  },
  methods: {
    async loadManagerData() {
      try {
        await this.$store.dispatch("fetchMe");
      } catch (e) {
        console.error("Error fetching company in menu:", e);
      }
      try {
        this.reservations = await fetchMyReservations();
      } catch (e) {
        console.error("Error fetching reservations in menu:", e);
      }
    },
    async togglePanel() {
      this.panelOpen = !this.panelOpen;
      // Los datos del cliente se piden al abrir el panel y una sola vez: no
      // vale una consulta por página para todos, sólo para quien lo despliega.
      if (this.panelOpen && !this.canManage && !this.me) {
        try {
          await this.$store.dispatch("fetchMe");
        } catch (e) {
          console.error("Error fetching user in menu:", e);
        }
      }
    },
    closePanel() {
      this.panelOpen = false;
    },
  },
};
</script>

<style scoped>
.theater-header {
  height: 120px;
  width: 100%;
  display: flex;
  align-items: center;
  background-color: transparent;
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.35));
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.45);
  position: relative;
  z-index: 20;
}

.header-inner {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 18px;
}

.header-left {
  display: flex;
  align-items: center;
}

.logo-link {
  display: inline-flex;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.logo-link:hover {
  transform: translateY(-1px);
}

.logo-img {
  height: 96px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.45));
}

.header-nav {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 34px;
  flex-wrap: wrap;
}

.header-link {
  color: var(--color-theater-text);
  text-decoration: none;
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  transition: opacity 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
}

.header-link:hover {
  opacity: 0.82;
  transform: translateY(-1px);
}

/* --- Nav dropdowns (hover) ---------------------------------------------- */
.nav-dd {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.nav-dd-trigger {
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.caret {
  font-size: 9px;
  opacity: 0.9;
}

/* Invisible bridge so hover survives the gap to the menu. */
.nav-dd::after {
  content: "";
  position: absolute;
  top: 100%;
  left: -10px;
  right: -10px;
  height: 18px;
}

.nav-dd-menu {
  position: absolute;
  top: calc(100% + 16px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 200px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.45);
  padding: 8px 0;
  display: none;
  z-index: 60;
}

.nav-dd:hover .nav-dd-menu {
  display: block;
}

.nav-dd-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 22px;
  text-decoration: none;
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  white-space: nowrap;
  transition: background 0.15s ease;
}

.nav-dd-item:hover {
  background: #f4f4f4;
}

.nav-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #c8102e;
  color: #fff;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
}

.header-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

/* --- Botón de login (anónimo) ------------------------------------------- */
.login-btn-header {
  color: #fff;
  text-decoration: none;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 11px 22px;
  border: none;
  border-radius: 8px;
  white-space: nowrap;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  box-shadow: 0 4px 12px rgba(200, 16, 46, 0.35);
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.login-btn-header:hover {
  background: linear-gradient(90deg, #d81532 0%, #b30f29 100%);
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.45);
}

/* --- Company panel ------------------------------------------------------- */
.company-menu {
  position: relative;
}

.role-badge {
  padding: 5px 11px;
  border-radius: 999px;
  background: #c8102e;
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.company-badge {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #fff;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
  transition: transform 0.2s ease;
}

.company-badge:hover {
  transform: translateY(-1px);
}

.company-badge-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.company-badge-fallback {
  color: #c8102e;
  font-family: var(--font-family-heading);
  font-weight: 800;
  font-size: 16px;
  letter-spacing: 0.5px;
}

.company-panel {
  position: absolute;
  top: calc(100% + 14px);
  right: 0;
  width: 264px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.5);
  padding: 18px 0 8px;
  z-index: 60;
}

.company-panel-close {
  position: absolute;
  top: 12px;
  right: 14px;
  background: transparent;
  border: none;
  color: #9a9a9a;
  font-size: 14px;
  cursor: pointer;
}

.company-panel-head {
  padding: 2px 22px 14px;
}

.company-panel-name {
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-weight: 800;
  font-size: 16px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.company-panel-sub {
  color: #c8102e;
  font-family: var(--font-family-heading);
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 1px;
  margin-top: 2px;
}

.company-panel-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 13px 22px;
  text-decoration: none;
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  border-top: 1px solid #ececec;
  transition: background 0.15s ease;
}

.company-panel-item:hover {
  background: #f4f4f4;
}

@media (max-width: 960px) {
  .header-inner {
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 10px 20px 14px;
    text-align: center;
  }

  .header-left,
  .header-right {
    justify-content: center;
  }

  .header-nav {
    justify-content: center;
    gap: 18px;
  }

  .company-panel {
    right: 50%;
    transform: translateX(50%);
  }

  .nav-dd-menu {
    left: 50%;
  }
}

@media (max-width: 600px) {
  .logo-img {
    height: 72px;
  }

  .header-link {
    font-size: 11px;
  }
}
</style>
